<script setup>
import { RouterLink } from 'vue-router'
import HeadlineFraunces from '@/design/primitives/HeadlineFraunces.vue'
import ButtonSeed from '@/design/primitives/ButtonSeed.vue'
import PanelOrganic from '@/design/primitives/PanelOrganic.vue'
import IconArrowRight from '@/design/icons/IconArrowRight.vue'
</script>

<template>
  <section class="about">
    <div class="about__container">
      <PanelOrganic tone="strong" :grain="true" :padded="true" class="about__panel">
        <div class="about__head">
          <span class="about__kicker">EVOLUXU · DOSSIER</span>
          <HeadlineFraunces level="h2">About <em>the</em> Server</HeadlineFraunces>
        </div>

        <div class="about__body">
          <p class="about__lead">
            <span class="about__lead-em">Evoluxu.com</span> is a premium ARK: Survival Ascended
            server built for both new and experienced players. We focus on stability, fair
            gameplay, and an engaging long-term experience.
          </p>
          <p class="about__lead">
            With optimized settings, active administration, and regular updates, Evoluxu
            delivers a high-quality survival environment.
          </p>

          <RouterLink to="/market" custom v-slot="{ navigate }">
            <ButtonSeed size="lg" @click="navigate" class="about__cta">
              Visit Store
              <IconArrowRight class="about__cta-icon"/>
            </ButtonSeed>
          </RouterLink>
        </div>

      </PanelOrganic>
    </div>
  </section>
</template>

<style scoped>
.about {
  position: relative;
  padding: var(--sp-32) var(--sp-12);
}
.about__container {
  max-width: var(--container-wide);
  margin: 0 auto;
}
.about__panel {
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--sp-12);
  text-align: center;
  justify-items: center;
}

.about__head {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--sp-3);
}
.about__kicker {
  font-family: var(--font-display);
  font-size: var(--fs-12);
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: var(--gold);
}

.about__body {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--sp-6);
  max-width: 78ch;
}
.about__lead {
  margin: 0;
  font-size: clamp(var(--fs-18), 1.4vw, var(--fs-21));
  line-height: 1.6;
  color: var(--ink);
  opacity: 0.92;
}
.about__lead-em { color: var(--gold); font-weight: 500; }

.about__cta {
  margin-top: var(--sp-2);
  width: fit-content;
}
.about__cta-icon { width: 18px; height: 18px; }

.about__stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0;
  width: 100%;
  border-top: 1px solid rgba(229, 199, 107, 0.30);
  padding-top: var(--sp-8);
}
.about__stat {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--sp-3);
  padding: 0 var(--sp-6);
  position: relative;
  text-align: center;
}
.about__stat:not(:last-child)::after {
  content: "";
  position: absolute;
  top: 6px;
  bottom: 6px;
  right: 0;
  width: 1px;
  background: var(--ash-strong);
}

.about__stat-num {
  font-family: var(--font-display);
  font-size: clamp(var(--fs-44), 4vw, var(--fs-60));
  line-height: 0.95;
  color: var(--gold);
  letter-spacing: 0.01em;
}
.about__stat-rule {
  width: 32px;
  height: 1.5px;
  background: var(--gold);
  opacity: 0.7;
}
.about__stat-label {
  font-family: var(--font-display);
  font-size: var(--fs-14);
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--ink);
}
.about__stat-desc {
  margin: 0;
  font-size: var(--fs-14);
  line-height: 1.5;
  color: var(--ink-soft);
}

@media (max-width: 1024px) {
  .about { padding: var(--sp-24) var(--sp-6); }
}
@media (max-width: 560px) {
  .about__stats { grid-template-columns: 1fr; gap: var(--sp-6); }
  .about__stat { padding: 0 0 var(--sp-6) 0; border-bottom: 1px solid var(--ash); }
  .about__stat:last-child { border-bottom: 0; padding-bottom: 0; }
  .about__stat::after { display: none !important; }
}
</style>
